import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from "motion/react";
import { FeaturedVenueCard } from './FeaturedVenueCard';
import { EventCard } from './EventCard';
import { GallerySlider } from './GallerySlider';
import { ArtistScheduleSection } from './ArtistScheduleSection';
import { likeService } from '@/features/likes/services/likeService';
import type { ArtistContentSectionProps } from '@/features/artists/types/types';

/**
 * Main content section for artist page
 */
export const ArtistContentSection: React.FC<ArtistContentSectionProps> = ({ artist, events, isAuthenticated }) => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  const galleryMedia = artist.media?.filter(m => m.rank > 0) || [];
  const [likedEventIds, setLikedEventIds] = useState<Set<string>>(new Set());

  // Batch check likes for all events on mount
  useEffect(() => {
    const checkEventLikes = async () => {
      if (!events || events.length === 0 || !isAuthenticated) return;

      try {
        const eventIds = events.map(e => e.event.id);
        const response = await likeService.checkLikes({
          entity_ids: eventIds,
          entity_type: 'events',
        });

        if (response.liked_map) {
          const likedIds = new Set<string>(
            Object.entries(response.liked_map)
              .filter(([, isLiked]) => isLiked)
              .map(([id]) => id)
          );
          setLikedEventIds(likedIds);
        }
      } catch (error) {
        console.error('Failed to check event likes:', error);
      }
    };

    checkEventLikes();
  }, [events, isAuthenticated]);

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.section
      ref={sectionRef}
      className="max-w-5xl mx-auto px-8 py-20"
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <motion.div variants={itemVariants}>
        <h2 className="text-3xl font-serif font-medium text-white mb-4">About</h2>
        <p className="text-gray-300 leading-relaxed whitespace-pre-line">
          {artist.description}
        </p>
      </motion.div>

      <motion.div variants={itemVariants} className="mt-16">
        <h2 className="text-3xl font-serif font-medium text-white mb-4">Featured Venue</h2>
        <FeaturedVenueCard artist={artist} />
      </motion.div>

      {galleryMedia.length > 0 && (
        <motion.div variants={itemVariants} className="mt-16">
          <h2 className="text-3xl font-serif font-medium text-white mb-6">Gallery</h2>
          <GallerySlider galleryMedia={galleryMedia} />
        </motion.div>
      )}

      {events && events.length > 0 && (
        <motion.div variants={itemVariants} className="mt-16">
          <h2 className="text-3xl font-serif font-medium text-white mb-6">Upcoming Events</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {events.map((eventData) => (
              <EventCard 
                key={eventData.event.id} 
                eventData={eventData} 
                initialIsLiked={likedEventIds.has(eventData.event.id)}
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* Schedule Section */}
      <ArtistScheduleSection artistId={artist.id} />
    </motion.section>
  );
};
